import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import infoSelected from '../../../assets/info-blue-light.svg';
import info from '../../../assets/info-white.svg';

import { WhatsCoveredLink } from '../WhatsCoveredLink';
import { PowerUpTitle } from './PowerUpTitle';

import {
  currencyFormat,
} from '../../../util';

import styles from './PowerUpCard.module.css';
import { RadioButton } from '../../common/RadioButton';
import { useProp103Features, useResponsive } from '../../../hooks';
import { removePowerupIdError } from '../../../actions';
import { PowerUpCardTest } from './PowerUpCardTest';

const PowerUpCardCurrent = ({
  bundleCoverageClick = () => { },
  bundleModifierAdd = () => { },
  bundleModifierCheckSelection = () => { },
  content,
  error = false,
  handleAddPowerup,
  handleRemovePowerup,
  hideInfoImage,
  onClickWhatsCovered,
  petQuoteSelected,
  powerUpInfo,
  price,
  selected = null,
  title,
}) => {
  const { t } = useTranslation('quoting');
  const [isMounted, setMounted] = useState(false);
  const [localSelected, setLocalSelected] = useState(selected);
  const { isMobile } = useResponsive();
  const dispatch = useDispatch();
  const prop103 = useProp103Features();

  const activeBundleModifiers = powerUpInfo.BundleInsuranceModifiersEB
    ?.filter((modifier) => modifier.IsVisible && modifier.IsActive) ?? [];
  const hasMultipleOptions = activeBundleModifiers.length >= 2;

  useEffect(() => {
    setLocalSelected(selected);
  }, [petQuoteSelected, selected]);

  useEffect(() => {
    if (!isMounted) {
      setMounted(true);
    }
  }, [isMounted]);

  const handleInfoPowerup = ({ powerUp }) => () => {
    if (powerUp.PMSModifierId) {
      onClickWhatsCovered();
    }
  };

  function renderInfoImage() {
    if (hideInfoImage) {
      return null;
    }

    return (
      <WhatsCoveredLink
        ariaControls={`powerUp${powerUpInfo?.PMSModifierId}modalContainer`}
        className={styles.whatsCovered}
        icon={localSelected ? infoSelected : info}
        id={title}
        onClick={handleInfoPowerup({ powerUp: powerUpInfo })}
      />
    );
  }

  const handleAddPowerupCallback = useCallback((event) => {
    if (!localSelected && handleAddPowerup) {
      handleAddPowerup(event);
    }

    if (error) {
      dispatch(removePowerupIdError(powerUpInfo.Id));
    }
  }, [handleAddPowerup, localSelected, error, dispatch, powerUpInfo]);

  const handleRemovePowerupCallback = useCallback((event) => {
    if ((localSelected || localSelected === null) && handleRemovePowerup) {
      handleRemovePowerup(event);
    }
    if (error) {
      dispatch(removePowerupIdError(powerUpInfo.Id));
    }
  }, [dispatch, error, handleRemovePowerup, localSelected, powerUpInfo]);

  const handleAddPowerupOption = useCallback((event) => {
    if (!localSelected) {
      handleAddPowerupCallback(event);
    }
  }, [
    handleAddPowerupCallback,
    localSelected,
  ]);

  const handleRemovePowerupOption = useCallback((event) => {
    if (localSelected || localSelected === null) {
      handleRemovePowerupCallback(event);
    }
  }, [
    handleRemovePowerupCallback,
    localSelected,
  ]);

  function renderOptions() {
    const contentModifiers = powerUpInfo.BundleInsuranceModifiersEB
      ?.filter((modifier) => modifier.IsVisible && modifier.IsActive);
    if (contentModifiers && contentModifiers.length) {
      const bundleModifiers = [...contentModifiers].reverse();
      return bundleModifiers.map((item, index) => (
        <RadioButton
          key={item.Id}
          checked={!!bundleModifierCheckSelection(item.Id)}
          id={`${index}-${title.replace(/\s/g, '')}`}
          name={`pp-${powerUpInfo.Id}`}
          onChange={bundleModifiers.length >= 2
            ? bundleModifierAdd(
              {
                isSelected: !bundleModifierCheckSelection(item.Id),
                powerUp: item,
              },
            )
            : handleAddPowerupOption}
        >
          <div className={styles.optionWrapper}>
            <span className={styles.option}>
              {t('powerUpsSection.yesPowerup', {
                powerup: `${powerUpInfo.TitleText} ${item.TitleText}`,
              })}

              <span>
                ${currencyFormat({ value: item.FeeAmount })}

                {isMobile
                  ? t('powerUpsSection.paymentCycle.monthly')
                  : t('powerUpsSection.paymentCycle.monthlyLong')}
              </span>
            </span>

            {bundleModifiers.length >= 2 && (
              <WhatsCoveredLink
                className="Powerup-wellness-link-content-radio"
                icon={bundleModifierCheckSelection(item.Id)
                  ? infoSelected : info}
                id={`whatsCovered${item.Id}`}
                onClick={bundleCoverageClick(item)}
              />
            )}
          </div>
        </RadioButton>
      ));
    }
    return (
      <RadioButton
        checked={!!localSelected}
        id={`yes-${title.replace(/\s/g, '')}`}
        name={`pp-${powerUpInfo.Id}`}
        onChange={handleAddPowerupOption}
      >
        <span className={styles.option}>
          {t('powerUpsSection.yesPowerup', { powerup: powerUpInfo.TitleText })}

          <span>
            ${currencyFormat({ value: price })}

            {isMobile
              ? t('powerUpsSection.paymentCycle.monthly')
              : t('powerUpsSection.paymentCycle.monthlyLong')}
          </span>
        </span>
      </RadioButton>
    );
  }

  return (
    <section
      aria-labelledby={`Powerup-card-title-${powerUpInfo.Id}`}
      className={`${localSelected
        ? styles.containerSelected
        : styles.container}`
        + ` ${prop103 ? styles.containerProp103 : ''}`
        + ` ${hasMultipleOptions ? styles.hasMultipleOptions : ''}`}
      data-error={error}
      id={`Powerup-card-section-${powerUpInfo.Id}`}
    >
      <div className={styles.bodyContent}>
        <PowerUpTitle
          id={`Powerup-card-title-${powerUpInfo.Id}`}
          title={title}
        >
          {renderInfoImage()}
        </PowerUpTitle>

        <div className={styles.content}>
          <section>
            {content}

            <div
              className={`${styles.actionsRadios}`
                + ` ${hideInfoImage ? styles.actionsEnd : ''}`}
            >
              {renderOptions()}

              <RadioButton
                checked={localSelected === false}
                id={`no-${title.replace(/\s/g, '')}`}
                name={`pp-${powerUpInfo.Id}`}
                onChange={handleRemovePowerupOption}
              >
                <span className={styles.option}>
                  {t('powerUpsSection.noPowerup')}
                </span>
              </RadioButton>

              <span aria-live="polite" className={styles.error}>
                {error ? t('powerUpsSection.required', { powerup: title }) : ''}
              </span>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

const PowerUpCard = ({ isWellness, ...props }) => {
  const isProp103 = useProp103Features();

  return isProp103
    ? <PowerUpCardTest {...props} />
    : <PowerUpCardCurrent {...props} />;
};

export { PowerUpCard };
